import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type PhoneJsonEditLog } from "@/lib/api";
import { formatJsonEditWhen, jsonEditKindLabel } from "@/lib/json-edits";

export function JsonEditsTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex flex-col gap-0">
        <div className="flex gap-4 border-b border-border px-4 py-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-36" />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-0"
          >
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-36" />
          </div>
        ))}
      </div>
    </div>
  );
}

function changeLine(change: {
  kind: string;
  label: string;
  from: string | null;
  to: string | null;
  fromIndex?: number;
  toIndex?: number;
}): string {
  if (change.kind === "moved") {
    return `${change.label}：第${change.fromIndex}项 → 第${change.toIndex}项`;
  }
  if (change.kind === "added") {
    return `${change.label}：${change.to ?? ""}`;
  }
  if (change.kind === "removed") {
    return `${change.label}：${change.from ?? ""}`;
  }
  return `${change.label}：${change.from ?? ""} → ${change.to ?? ""}`;
}

function JsonEditRow({ log }: { log: PhoneJsonEditLog }) {
  const [open, setOpen] = useState(false);
  const changes = log.payload?.changes ?? [];
  const omitted = log.payload?.omitted ?? 0;
  const canOpen = changes.length > 0;

  return (
    <>
      <TableRow>
        <TableCell className="whitespace-nowrap text-muted-foreground">
          {formatJsonEditWhen(log.occurredAt)}
        </TableCell>
        <TableCell>
          {canOpen ? (
            <button
              type="button"
              className="text-left"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
            >
              <p className="text-sm text-foreground">{log.summary}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {log.locale} · v{log.versionN}
                {open ? " · 收起" : " · 展开字段"}
              </p>
            </button>
          ) : (
            <>
              <p className="text-sm text-foreground">{log.summary}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {log.locale} · v{log.versionN}
              </p>
            </>
          )}
        </TableCell>
        <TableCell className="font-medium">{log.actorEmail}</TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell colSpan={3} className="bg-muted/40">
            <ul className="flex flex-col gap-1.5 py-1">
              {changes.map((change, index) => (
                <li
                  key={`${change.path}:${change.kind}:${index}`}
                  className="text-sm text-foreground"
                >
                  <span className="text-xs text-muted-foreground">
                    {jsonEditKindLabel(change.kind)}
                  </span>
                  <span className="mx-1.5">·</span>
                  <span className="break-all">{changeLine(change)}</span>
                </li>
              ))}
              {omitted > 0 ? (
                <li className="text-xs text-muted-foreground">
                  另有 {omitted} 处未列出
                </li>
              ) : null}
            </ul>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

export function JsonEditsTable({
  logs,
  emptyTitle = "暂无修改记录",
  emptyDescription = "在编辑器里保存 Phone JSON 后，会在这里留下谁改了哪些字段。",
}: {
  logs: PhoneJsonEditLog[];
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  if (logs.length === 0) {
    return (
      <Empty className="border border-border">
        <EmptyHeader>
          <EmptyTitle>{emptyTitle}</EmptyTitle>
          <EmptyDescription>{emptyDescription}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-52">日期时间</TableHead>
            <TableHead>改了什么</TableHead>
            <TableHead className="w-52">操作人账号</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <JsonEditRow key={log.id} log={log} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
